import forIn from 'lodash/forIn';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
/* eslint-disable react/no-unused-prop-types */
import React, {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import videojs from 'video.js';

import AudioPlayer from '../../components/AudioPlayer';
import VideoPlayer from '../../components/VideoPlayer';
import createUrl from '../../util/createUrl';
import { AUDIO_TYPE, VIDEO_TYPE } from '../MediaPlayer/types';
import GAEventTracking from './GA-event-tracking';
import getPlayerOptions from './getPlayerOptions';
import enableKeyboardShortcuts from './util/aria-tabbed';
import moveButtonAfter from './util/move-videojs-button';
// NOTE: maybe nice to put theis Utils in the global utils
import { initializePlugin, preparePluginOptions } from './util/plugin';

// Turn back on when ready
videojs.log.level('off');

/**
 * Makes sure the plugin configuration that is passed to
 * this component are initialized.
 */
const initializePlugins = (playerEl, pluginOptionsArg) => {
  pluginOptionsArg.forEach(({ name, options }) =>
    initializePlugin({
      player: playerEl,
      name,
      options,
    })
  );
};

const moveButtonToTop = (options) => {
  const {
    player,
    name,
    timeout = 10,
    currentRetry = 0,
    maxRetries = 100,
  } = options;

  const top = player.el().querySelector('.js-top-icons');
  const button = player.controlBar.children().find((el) => {
    // eslint-disable-next-line no-underscore-dangle
    return el.name_ === name;
  });

  if (top && button) {
    top.prepend(button.el());

    return;
  }

  if (maxRetries > currentRetry) {
    setTimeout(
      () =>
        moveButtonToTop({
          ...options,
          currentRetry: currentRetry + 1,
        }),
      timeout
    );
  }
};

export const BNNVARAMediaPlayer = (props) => {
  const {
    // unused props
    // title = null,
    programTitle = null,
    // vertical = false,
    // width = 560,
    // height = 315,
    // genres = [],
    // broadcasters = null,
    duration = null,
    mediaId,
    id = 'bnnvara-media',
    poster = null,
    loadingPosterImage = null,
    source = [],
    autoplay = false,
    isMobile = false,
    plugins = {},
    subtitles = [],
    cta = null,
    type,
    userId = false,
    isFallback = false,
  } = props;

  const nodeId = uniqueId(id);
  const [pluginOptions, setpluginOptions] = useState([]);
  const [videoIsInitialized, setVideoIsInitialized] = useState(null);
  const videoNode = createRef(null);

  const player = useRef(null);
  const isVideo = type === 'video';
  const slugMedia =
    type === 'video' && programTitle
      ? createUrl(programTitle, type, mediaId)
      : null;

  const createVideoJsInstance = useCallback(
    // eslint-disable-next-line no-shadow
    (typeArg, autoplay) => {
      const playerOptions = getPlayerOptions(typeArg);
      // init video
      player.current = videojs(
        videoNode.current,
        {
          ...playerOptions,
          autoplay,
        },
        // onPlayerReady
        () => {
          videojs.log('3.2 Your player is ready!');
          setVideoIsInitialized(true);
        }
      );
      videojs.log('3.1 init plugins first batch before createVideoInstance');
      // Some plug-ins require an immediate initialization
      initializePlugins(player.current, pluginOptions);
    },
    [pluginOptions, videoNode]
  );

  /**
   * On the first render we set the pluginOptions
   */
  useEffect(() => {
    // eslint-disable-next-line func-names
    (async function () {
      const loaders = [];
      forIn(plugins, (config, name) => {
        loaders.push(
          preparePluginOptions({
            name,
            config,
            player: player.current,
            props,
            state: {},
          })
        );
      });

      const allLoaders = await Promise.all(loaders);
      videojs.log('1. setting the plugin options');
      setpluginOptions(allLoaders);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * If pluginOptions is set then we initialize video and plugins
   * Note: some plugins need to be init before the video init
   */
  useEffect(() => {
    if (pluginOptions.length > 0) {
      videojs.log('2 creating the video instance');
      createVideoJsInstance(type, autoplay);
    }
    return () => {
      if (player.current) {
        player.current.dispose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginOptions]);

  // Post video init functionality
  useEffect(() => {
    if (videoIsInitialized && player.current) {
      videojs.log('4. post video init');
      // Some plug-ins require initialization when the player is ready
      initializePlugins(player.current, pluginOptions);

      // Enable (accessibility) keyboard shortcuts
      enableKeyboardShortcuts(player.current);

      // Move Airplay button to the front
      moveButtonAfter({
        sourceName: 'AirplayToggle',
        afterName: 'CustomControlSpacer',
        player: player.current,
      });

      moveButtonToTop({
        name: 'ChromeCastButton',
        player: player.current,
      });

      // @TODO: search a nicer solution to the separation between video and audio
      if (isVideo) {
        /**
         * GA tracking
         */
        GAEventTracking(player.current, duration, mediaId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoIsInitialized]);

  /**
   * Updates the internal video.js instance based on the props.
   */
  useEffect(() => {
    if (videoIsInitialized && player.current) {
      if (source.length) {
        player.current.src(source);
      }

      if (source.length) {
        player.current.src(source);
      }

      if (poster) {
        player.current.poster(poster);
      }

      if (Array.isArray(subtitles)) {
        subtitles.forEach((subtitle) =>
          player.current.addRemoteTextTrack(
            {
              ...subtitle,
              default:
                isMobile && subtitle.language === player.current.language(),
            },
            true
          )
        );
      }
    }
  }, [
    createVideoJsInstance,
    isMobile,
    pluginOptions,
    poster,
    source,
    subtitles,
    type,
    videoIsInitialized,
  ]);

  return (
    <div data-vjs-player>
      {isVideo ? (
        <VideoPlayer
          ref={videoNode}
          nodeId={nodeId}
          loadingPosterImage={loadingPosterImage}
          mediaId={mediaId}
          duration={duration}
          detailPageUrl={slugMedia}
          player={player.current}
          brand={programTitle}
          cta={cta}
          slugMedia={slugMedia}
          userId={userId}
          isFallback={isFallback}
        />
      ) : (
        <AudioPlayer ref={videoNode} nodeId={nodeId} />
      )}
    </div>
  );
};

BNNVARAMediaPlayer.propTypes = {
  // HTML identifier which will be appended with a unique id.
  id: PropTypes.string,
  // The type (audio or video)
  type: PropTypes.oneOf([VIDEO_TYPE, AUDIO_TYPE]).isRequired,
  // The media ID
  mediaId: PropTypes.string,
  // The initial poster to show.
  poster: PropTypes.string,
  // The initial poster to show.
  loadingPosterImage: PropTypes.string,
  // The initial source to show.
  source: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      ratio: PropTypes.string.isRequired,
    })
  ),
  // Whether the player should be playing.
  autoplay: PropTypes.bool,
  programTitle: PropTypes.string,
  // Whether or not the player is running on mobile
  isMobile: PropTypes.bool,
  // The plugins to load for this player
  // `imports` should be an array of external modules to load
  // `mapPropStateToOptions` can either be an object with options or a function
  // that resolves to an options object.
  plugins: PropTypes.objectOf(
    PropTypes.shape({
      mapPropStateToOptions: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.object,
      ]),
    })
  ),
  // Subtitle tracks to load with the video.
  subtitles: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      language: PropTypes.string.isRequired,
      srcLang: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  genres: PropTypes.arrayOf(PropTypes.shape()),
  broadcasters: PropTypes.string,
  duration: PropTypes.number,
  cta: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  userId: PropTypes.string,
  isFallback: PropTypes.bool,
  // Not being used
  // // The title of the video.
  // title: PropTypes.string,
  // // Marks the player as a vertical.
  // vertical: PropTypes.bool,
  // // Default width of the video.
  // width: PropTypes.number,
  // // Default height of the video.
  // height: PropTypes.number,
};
